import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCar } from '@/contexts/CarContext';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthProvider';
import PageLayout from '@/components/PageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';

const ShareCarPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { acceptShareLink } = useCar();
  const { t } = useI18n();
  
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showNicknameDialog, setShowNicknameDialog] = useState(false);
  const [nickname, setNickname] = useState('');
  const [savingNickname, setSavingNickname] = useState(false);
  const [acceptedCarId, setAcceptedCarId] = useState<string | null>(null);
  const [invitationData, setInvitationData] = useState<{
    creatorName: string;
    carMake: string;
    carModel: string;
    carLicensePlate: string;
    carNickname: string | null;
  } | null>(null);

  // Set default meta tags on mount for better link preview
  useEffect(() => {
    const shareUrl = window.location.href;
    const defaultTitle = t('shareCarPage.metaDefaultTitle');
    const defaultDescription = t('shareCarPage.metaDefaultDescription');

    // Update document title
    document.title = defaultTitle;

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', defaultDescription);
    }

    // Update Open Graph tags
    const updateMetaTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    updateMetaTag('og:title', defaultTitle);
    updateMetaTag('og:description', defaultDescription);
    updateMetaTag('og:url', shareUrl);
    updateMetaTag('og:type', 'website');
    updateMetaTag('og:image', 'https://app.onwheels.live/On_Wheels_logo.png');
    updateMetaTag('og:site_name', 'On Wheels');

    // Update Twitter Card tags
    const updateTwitterTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    updateTwitterTag('twitter:card', 'summary_large_image');
    updateTwitterTag('twitter:title', defaultTitle);
    updateTwitterTag('twitter:description', defaultDescription);
    updateTwitterTag('twitter:image', 'https://app.onwheels.live/On_Wheels_logo.png');
  }, [t]);

  // Update meta tags for social sharing
  useEffect(() => {
    if (!invitationData) return;

    const creatorName = invitationData.creatorName;
    const carInfo = `${invitationData.carMake} ${invitationData.carModel}`.trim() || t('shareCarPage.carFallback');
    const shareUrl = window.location.href;

    const metaTitle = t('shareCarPage.metaTitleInvited').replace('{creatorName}', creatorName).replace('{carInfo}', carInfo);
    const metaDesc = t('shareCarPage.metaDescriptionJoin').replace('{creatorName}', creatorName).replace('{carInfo}', carInfo);
    const metaAlt = t('shareCarPage.metaImageAlt').replace('{creatorName}', creatorName).replace('{carInfo}', carInfo);
    const twitterDesc = t('shareCarPage.twitterDescriptionHelp');

    // Update document title
    document.title = metaTitle;

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', metaDesc);
    }

    // Update Open Graph tags
    const updateMetaTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    updateMetaTag('og:title', metaTitle);
    updateMetaTag('og:description', metaDesc);
    updateMetaTag('og:url', shareUrl);
    updateMetaTag('og:type', 'website');
    updateMetaTag('og:image', 'https://app.onwheels.live/On_Wheels_logo.png');
    updateMetaTag('og:image:alt', metaAlt);
    updateMetaTag('og:site_name', 'On Wheels');

    // Update Twitter Card tags
    const updateTwitterTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    updateTwitterTag('twitter:card', 'summary_large_image');
    updateTwitterTag('twitter:title', metaTitle);
    updateTwitterTag('twitter:description', twitterDesc);
    updateTwitterTag('twitter:image', 'https://app.onwheels.live/On_Wheels_logo.png');

    // Cleanup function to restore defaults
    return () => {
      document.title = t('shareCarPage.siteDefaultTitle');
      const defaultDesc = document.querySelector('meta[name="description"]');
      if (defaultDesc) {
        defaultDesc.setAttribute('content', t('shareCarPage.siteDefaultDescription'));
      }
    };
  }, [invitationData, t]);

  useEffect(() => {
    const checkAuthAndLoadInvitation = async () => {
      if (!token) {
        setError(t('carSharing.invalidLink'));
        setLoading(false);
        return;
      }

      try {
        // Get session for authenticated request
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          // User not logged in - store returnTo in localStorage and redirect to login
          localStorage.setItem('auth-return-to', `/share/car/${token}`);
          navigate('/login');
          return;
        }

        // Fetch invitation and car details using Edge Function (can access auth metadata)
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
        
        const response = await fetch(
          `${supabaseUrl}/functions/v1/validate-invitation-token?token=${encodeURIComponent(token)}`,
          {
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'apikey': supabaseAnonKey,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          setError(errorData.error || t('carSharing.invalidLink'));
          setLoading(false);
          return;
        }

        const data = await response.json();

        if (!data.success) {
          setError(data.error || t('carSharing.invalidLink') || 'Invalid link');
          setLoading(false);
          return;
        }

        // Set invitation data with creator name and car details
        setInvitationData({
          creatorName: data.creator?.name || t('common.unknownUser'),
          carMake: data.car?.make || '',
          carModel: data.car?.model || '',
          carLicensePlate: data.car?.licensePlate || '',
          carNickname: data.car?.nickname || null,
        });

        setLoading(false);
      } catch (err: any) {
        console.error('Error checking auth:', err);
        setError(err.message || t('common.errorOccurred'));
        setLoading(false);
      }
    };

    checkAuthAndLoadInvitation();
  }, [token, navigate, t]);

  const handleAccept = async () => {
    if (!token) return;

    setAccepting(true);
    setError(null);

    try {
      const result = await acceptShareLink(token);
      setAcceptedCarId(result.carId);
      setSuccess(true);
      // Show nickname dialog instead of redirecting immediately
      setShowNicknameDialog(true);
    } catch (err: any) {
      console.error('Error accepting share:', err);
      setError(err.message || t('carSharing.linkExpiredMessage'));
      setAccepting(false);
    }
  };

  const handleSaveNickname = async () => {
    if (!user || !acceptedCarId) return;

    setSavingNickname(true);
    try {
      // Upsert user-specific nickname
      const { error: prefError } = await supabase
        .from('user_car_preferences')
        .upsert({
          user_id: user.id,
          car_id: acceptedCarId,
          nickname: nickname.trim() || null,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,car_id'
        });

      if (prefError) {
        console.error('Error saving nickname:', prefError);
        // Don't throw - just continue to redirect
      }
    } catch (err) {
      console.error('Error saving nickname:', err);
    } finally {
      setSavingNickname(false);
      setShowNicknameDialog(false);
      // Redirect to car detail page
      navigate(`/cars/${acceptedCarId}`, { replace: true });
    }
  };

  const handleSkipNickname = () => {
    setShowNicknameDialog(false);
    // Redirect to car detail page without saving nickname
    if (acceptedCarId) {
      navigate(`/cars/${acceptedCarId}`, { replace: true });
    }
  };

  if (loading) {
    return (
      <PageLayout title={t('carSharing.acceptInvite')}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </PageLayout>
    );
  }

  if (error && !success) {
    return (
      <PageLayout title={t('carSharing.acceptInvite')}>
        <div className="max-w-md mx-auto mt-8 px-4">
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button
            onClick={() => navigate('/cars')}
            className="w-full mt-4"
            variant="secondary"
          >
            {t('actions.close')}
          </Button>
        </div>
      </PageLayout>
    );
  }

  if (success && !showNicknameDialog) {
    return (
      <PageLayout title={t('carSharing.acceptInvite')}>
        <div className="max-w-md mx-auto mt-8 px-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-center mb-4">
                <CheckCircle2 className="h-16 w-16 text-green-600" />
              </div>
              <CardTitle className="text-center">
                {t('carSharing.inviteAccepted')}
              </CardTitle>
              <CardDescription className="text-center">
                {t('carSharing.inviteAcceptedDescription')}
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={t('carSharing.acceptInvite')}>
      <div className="max-w-md mx-auto mt-8 px-4">
        <Card>
          <CardHeader>
            <CardTitle>{t('carSharing.acceptInvite')}</CardTitle>
            <CardDescription>
              {t('carSharing.acceptInviteDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {invitationData && (
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                  <div>
                    <p className="text-sm text-gray-600 mb-1" style={{ fontFamily: 'Lato' }}>
                      {t('carSharing.sharedBy')}
                    </p>
                    <p className="font-semibold text-base" style={{ fontFamily: 'Lato' }}>
                      {invitationData.creatorName}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <img 
                      src="/icons/CarDetailPage/ShareCar.png" 
                      alt={t('carSharing.carImageAlt')} 
                      className="h-10 w-10 flex-shrink-0 object-contain"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-base" style={{ fontFamily: 'Lato' }}>
                        {invitationData.carMake} {invitationData.carModel}
                      </p>
                      <p className="text-sm text-gray-600" style={{ fontFamily: 'Lato' }}>
                        {invitationData.carLicensePlate}
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: 'Lato' }}>
                  {t('carSharing.acceptInviteExplanation').replace('{creatorName}', invitationData.creatorName)}
                </p>
              </div>
            )}

            {error && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/cars')}
                variant="secondary"
                className="flex-1"
                disabled={accepting}
              >
                {t('actions.cancel')}
              </Button>
              <Button
                onClick={handleAccept}
                className="flex-1"
                disabled={accepting}
              >
                {accepting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('carSharing.accepting')}
                  </>
                ) : (
                  t('carSharing.accept')
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Nickname Dialog */}
      <Dialog open={showNicknameDialog} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" style={{ fontFamily: 'Lato' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Lato', fontWeight: 700 }}>
              {t('carSharing.addNickname')}
            </DialogTitle>
            <DialogDescription style={{ fontFamily: 'Lato' }}>
              {t('carSharing.nicknameOnlyForYou')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nickname" style={{ fontFamily: 'Lato' }}>
                {t('car.nickname')}
              </Label>
              <Input
                id="nickname"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder={invitationData?.carNickname || `${invitationData?.carMake} ${invitationData?.carModel}`}
                style={{ fontFamily: 'Lato' }}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && nickname.trim()) {
                    handleSaveNickname();
                  }
                }}
              />
              {invitationData?.carNickname && (
                <p className="text-xs text-gray-500" style={{ fontFamily: 'Lato' }}>
                  {t('carSharing.currentNickname')}: {invitationData.carNickname}
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="flex flex-row gap-2 justify-between">
            <Button
              onClick={handleSkipNickname}
              variant="secondary"
              disabled={savingNickname}
              style={{ fontFamily: 'Lato' }}
            >
              {t('carSharing.skip')}
            </Button>
            <Button
              onClick={handleSaveNickname}
              disabled={savingNickname}
              style={{ fontFamily: 'Lato' }}
            >
              {savingNickname ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('actions.saving')}
                </>
              ) : (
                t('actions.save')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default ShareCarPage;

